/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902411790 — Заокруглення та оплата (roundSubunitsForPayment) */

import { useRef, useState } from 'react';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useDebouncedCallback } from '../../../shared/hooks/useDebouncedCallback';
import { roundSubunitsForPayment } from '../payment.utils';
import { useSetOrderAmount } from './useSetOrderAmount';

interface UseOrderAmountOptions {
    /** Delay before the amount POST fires — collapses a keystroke burst into one request. 0 = immediate. */
    debounceMs?: number;
}

interface UseOrderAmountReturn {
    isAmountSent: boolean;
    submitAmount: (amount: number) => void;
}

export const useOrderAmount = ({ debounceMs = 0 }: UseOrderAmountOptions = {}): UseOrderAmountReturn => {
    const [isAmountSent, setIsAmountSent] = useState(false);
    const order = useAppStore((s) => s.order);
    const updateOrder = useAppStore((s) => s.updateOrder);
    const { mutate: setOrderAmountMutate } = useSetOrderAmount();
    const latestSubmitIdRef = useRef(0);

    const persistAmount = useDebouncedCallback((orderId: string, amount: number, submitId: number) => {
        setOrderAmountMutate(
            { orderId, amount },
            {
                onSuccess: ({ data, requestedAmount }) => {
                    const isSupersededSubmit = submitId !== latestSubmitIdRef.current;
                    if (isSupersededSubmit) return;
                    // `data` is null on a skipped submit, so payment stays gated.
                    if (data?.amount === requestedAmount) setIsAmountSent(true);
                },
            },
        );
    }, debounceMs);

    const submitAmount = (amountInSubUnits: number) => {
        // Snap to integer subunits before persisting. Discount math can yield
        // fractional cents (e.g. 3 EUR × 33.34% → 199.98000000000002), which
        // serialises as a long float at PSP boundaries and gets rejected.
        const roundedSubUnitsAmount = roundSubunitsForPayment(amountInSubUnits);
        setIsAmountSent(false);
        // The charge is driven by the DB value, so a superseded submit's
        // response must never re-enable payment — only the latest one may
        // flip isAmountSent back to true (SWRN-1732).
        latestSubmitIdRef.current += 1;
        persistAmount(order.id as string, roundedSubUnitsAmount, latestSubmitIdRef.current);
        updateOrder((prev) => ({ ...prev, amount: roundedSubUnitsAmount }));
    };

    return { isAmountSent, submitAmount };
};
